import { Dropdown, Notification, Switcher, toast, Tooltip } from "components/ui";
import { PLACEMENT } from "components/ui/utils/constant";
import { useContext, useState } from "react";
import { TbChartCircles, TbMessage2Off, TbRectangleVertical, TbScreenshot } from "react-icons/tb";
import { LuSettings} from "react-icons/lu";
import { CompanyContext } from "contexts/CompanyContext";
import ThumbnailPicker from "./ThumbnailPicker";
import usePostValidator from "utils/hooks/usePostValidator";
import { getNetworkValidationIndex } from "utils/hooks/usePostValidator";

const MoreOptions = ({
    post,
    isEditable = true,
    onChange = (preferences) => void 0,
    onCoverEdit = (postIndex, cover) => void 0,
    icon = <LuSettings className="text-2xl" />,
    forceShowVideoCover = false,
    renderClassName = "",
}) => {
    
    const { companySocials } = useContext(CompanyContext);
    const integration = companySocials?.find(e => e.type == "tiktok");

    const [ showVideoCover, setShowVideoCover ] = useState(forceShowVideoCover);

    const { validationConstants } = usePostValidator();
    const networkValidationIndex = getNetworkValidationIndex(post?.network, post?.preferences?.media_product_type);
    const constants = validationConstants?.[networkValidationIndex];
    const showFrameTab = constants?.media_urls?.allow_frame_cover;
    const showFileTab = constants?.media_urls?.allow_file_cover;
    const [ preventClose, setPreventClose ] = useState(false);

    const resetState = () => {
        setShowVideoCover(false);
    }

    const handleTimestampSelect = (timestamp) => {
        onChange({ ...(post?.preferences || {}), video_cover_timestamp_ms: timestamp });
        resetState();
    }

    const handleCoverSelect = (url) => {
        onCoverEdit({ url });
        if (url) {
            toast.push(<Notification title={`Cover image selected`} type="success" />);
        } else {
            toast.push(<Notification title={`Cover image removed`} type="success" />);
        }
        // resetState();
    }

    if (!isEditable) return <></>;
    if (post?.network == "instagram" && post?.preferences?.media_product_type?.toLowerCase() != "feed") return <></>;
    if (post?.network == "instagram" && !post?.assets?.[0]?.video?.url) return <></>;
    if (post?.network == "instagram" && post?.assets?.length > 1) return <></>;

    return (
        <Dropdown
            preventClose={preventClose}
            placement={PLACEMENT.BOTTOM_END}
            parentClassName="!shrink-0"
            toggleClassName="h-full"
            menuClass={`${showVideoCover || forceShowVideoCover ? `w-auto` : `w-[300px]`} p-0 overflow-y-auto`}
            onClick={resetState}
            disabled={!isEditable || false}
            renderTitle={
                <div className={`h-full w-full !p-3 flex items-center justify-center border-b border-l border-gray-100 dark:border-gray-700 cursor-pointer ${isEditable && `hover:bg-gray-50 dark:hover:bg-gray-700`} ${renderClassName}`}>
                    {icon}
                </div>
            }
        >
            {showVideoCover || forceShowVideoCover
                ? <ThumbnailPicker 
                    post={post}
                    onSelectVideoCoverTimestamp={handleTimestampSelect}
                    onSelectCoverImage={handleCoverSelect}
                    showFrameTab={showFrameTab}
                    showFileTab={showFileTab}
                    setPreventClose={setPreventClose}
                />
                : <>
                    <Dropdown.Item className="!p-0 hidden" />
                    {!!post?.assets?.[0]?.video?.url && <Dropdown.Item className="!p-0 mt-2">
                        <div onClick={(e) => { setShowVideoCover(true); e.stopPropagation(); }} className="w-full h-ful flex items-center gap-2 cursor-pointer py-2 px-4">
                            <TbScreenshot className='h-6 md:text-2xl text-primary-600' />
                            {post?.cover?.url ? <span>Change video cover...</span> : <span>Select video cover...</span>}
                        </div>
                    </Dropdown.Item>}
                    {post?.network == "tiktok" && <>
                        <Dropdown.Item variant="divider" />
                        <Dropdown.Item className={`!p-0`}>
                            <div className="!py-3 px-4 w-full flex items-center gap-3 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                <Switcher
                                    className={"zoom-70"}
                                    checked={!!post?.preferences?.disable_comment}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => onChange({ ...(post?.preferences || {}), disable_comment: !!post?.preferences?.disable_comment ? 0 : 1 })}
                                    checkedContent={<div className="text-lg"><TbMessage2Off className='text-white' /></div>}
                                    disabled={integration?.preferences?.comment_disabled}
                                />
                                <Tooltip placement="bottom" disabled={!integration?.preferences?.comment_disabled} title={integration?.preferences?.comment_disabled && `You must change your TikTok's user privacy settings to change this setting.`} wrapperClass={`${integration?.preferences?.comment_disabled && `!cursor-not-allowed opacity-[0.5]`}`}>
                                    <div onClick={() => !integration?.preferences?.comment_disabled && onChange({ ...(post?.preferences || {}), disable_comment: !!post?.preferences?.disable_comment ? 0 : 1 })}>
                                        <span>Disable Comments</span>
                                    </div>
                                </Tooltip>
                            </div>
                        </Dropdown.Item>
                        <Dropdown.Item className={`!p-0`}>
                            <div className="!py-3 px-4 w-full flex items-center gap-3 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                <Switcher
                                    className={"zoom-70"}
                                    checked={!!post?.preferences?.disable_duet}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => onChange({ ...(post?.preferences || {}), disable_duet: !!post?.preferences?.disable_duet ? 0 : 1 })}
                                    checkedContent={<div className="text-lg"><TbChartCircles className='text-white' /></div>}
                                    disabled={integration?.preferences?.duet_disabled}
                                />
                                <Tooltip placement="bottom" disabled={!integration?.preferences?.duet_disabled} title={integration?.preferences?.duet_disabled && `You must change your TikTok's user privacy settings to change this setting.`} wrapperClass={`${integration?.preferences?.duet_disabled && `!cursor-not-allowed opacity-[0.5]`}`}>
                                    <div onClick={() => !integration?.preferences?.duet_disabled && onChange({ ...(post?.preferences || {}), disable_duet: !!post?.preferences?.disable_duet ? 0 : 1 })}>
                                        <span>Disable Duet</span>
                                    </div>
                                </Tooltip>
                            </div>
                        </Dropdown.Item>
                        <Dropdown.Item className={`!p-0`}>
                            <div className="!py-3 px-4 w-full flex items-center gap-3 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                <Switcher
                                    className={"zoom-70"}
                                    checked={!!post?.preferences?.disable_stitch}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => onChange({ ...(post?.preferences || {}), disable_stitch: !!post?.preferences?.disable_stitch ? 0 : 1 })}
                                    checkedContent={<div className="text-lg"><TbRectangleVertical className='text-white' /></div>}
                                    disabled={integration?.preferences?.stitch_disabled}
                                />
                                <Tooltip placement="bottom" disabled={!integration?.preferences?.stitch_disabled} title={integration?.preferences?.stitch_disabled && `You must change your TikTok's user privacy settings to change this setting.`} wrapperClass={`${integration?.preferences?.stitch_disabled && `!cursor-not-allowed opacity-[0.5]`}`}>
                                    <div onClick={() => !integration?.preferences?.stitch_disabled && onChange({ ...(post?.preferences || {}), disable_stitch: !!post?.preferences?.disable_stitch ? 0 : 1 })}>
                                        <span>Disable Stitch</span>
                                    </div>
                                </Tooltip>
                            </div>
                        </Dropdown.Item>
                    </>}
                </>

            }
        </Dropdown>
    )
}

export default MoreOptions;